import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileText, Loader2 } from "lucide-react";
import { useSupplements } from "@/hooks/use-supplements";
import { Comparison, ComparisonStatistics } from "@/api/generated/client/models";
import { useToast } from "@/hooks/use-toast";
import { ComparisonSelector } from "./ComparisonSelector";
import { SupplementPricing } from "./SupplementPricing";
import { useEstimateSupplementCost } from "@/hooks/use-billing";
import { Combobox } from "@/components/ui/combobox";
import { Label } from "@/components/ui/label";
import { Claim } from "@/api/generated/client/models";
import { US_STATE_OPTIONS as US_STATES } from "@/lib/us-states";

// Utility function to extract state code from address
function extractStateFromAddress(address?: string | null): string | null {
    if (!address) return null;

    // Match common state code patterns in addresses
    // Looks for 2-letter uppercase codes (possibly followed by zip)
    const stateMatch = address.match(/\b([A-Z]{2})(?:\s+\d{5})?\s*$/i);
    if (stateMatch) {
        const stateCode = stateMatch[1].toUpperCase();
        // Verify it's a valid US state code
        if (US_STATES.some(s => s.value === stateCode)) {
            return stateCode;
        }
    }
    return null;
}

interface SupplementModalProps {
    claimId: number;
    comparisons: Comparison[];
    claim?: Claim | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function SupplementModal({ claimId, comparisons, claim, open, onOpenChange }: SupplementModalProps) {
    const { createSupplementAsync, isCreating } = useSupplements(claimId);
    const { toast } = useToast();
    const { mutateAsync: estimateCost, data: costEstimate } = useEstimateSupplementCost();

    const [selectedComparison, setSelectedComparison] = useState<Comparison | null>(null);
    const [selectedStats, setSelectedStats] = useState<ComparisonStatistics | null>(null);
    const [selectedState, setSelectedState] = useState<string | null>(null);

    const defaultState = claim?.propertyAddress ? extractStateFromAddress(claim.propertyAddress) : null;

    // Initialize state when modal opens (if not already set by user)
    useEffect(() => {
        if (open && selectedState === null) {
            const extractedState = claim?.propertyAddress ? extractStateFromAddress(claim.propertyAddress) : null;
            // Validate that the extracted state is a valid US state code
            if (extractedState && US_STATES.some(s => s.value === extractedState)) {
                setSelectedState(extractedState);
            }
        }
    }, [open, claim?.propertyAddress, selectedState]);

    // Fetch cost estimate when stats change
    useEffect(() => {
        if (selectedStats?.unmatchedContractorCount !== undefined) {
            estimateCost(selectedStats.unmatchedContractorCount);
        }
    }, [selectedStats?.unmatchedContractorCount, estimateCost]);

    // Reset state when closing
    const handleOpenChange = (newOpen: boolean) => {
        if (!newOpen) {
            setSelectedComparison(null);
            setSelectedStats(null);
            setSelectedState(null);
        }
        onOpenChange(newOpen);
    };

    const handleSelectComparison = (comparison: Comparison | null, stats: ComparisonStatistics | null) => {
        setSelectedComparison(comparison);
        setSelectedStats(stats);
    };

    const handleCreateSupplement = async () => {
        if (!selectedComparison) {
            toast({
                title: "Error",
                description: "Please select a comparison",
                variant: "destructive"
            });
            return;
        }

        try {
            // Create the supplement and start the workflow
            // Backend automatically starts Temporal workflow which will:
            // 1. Analyze the comparison
            // 2. Generate supplement recommendations
            // 3. Record token usage and charge to Stripe subscription (on completion)
            await createSupplementAsync({
                claimId,
                comparisonId: selectedComparison.id,
                state: selectedState
            });

            toast({
                title: "Supplement Started",
                description: "Supplement is being generated in the background. You'll be notified when it's complete."
            });
            handleOpenChange(false);

        } catch (error) {
            console.error(error);
            toast({
                title: "Error",
                description: error instanceof Error ? error.message : "Failed to create supplement",
                variant: "destructive"
            });
        }
    };

    const canCreate = !!selectedComparison && !!selectedState && !isCreating;

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90dvh] md:max-h-[90vh] flex flex-col p-0 gap-0">
                <DialogHeader className="px-6 py-4 border-b">
                    <DialogTitle className="flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        Create New Supplement
                    </DialogTitle>
                    <DialogDescription>
                        Select a completed comparison to run a supplement analysis on. This will analyze any changes or additions to the claim.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-6">
                    {/* Comparison Selection */}
                    <div className="flex flex-col gap-3">
                        <ComparisonSelector
                            comparisons={comparisons}
                            selectedComparison={selectedComparison}
                            onSelectComparison={handleSelectComparison}
                        />
                    </div>

                    {/* State Selection */}
                    <div className="space-y-2 shrink-0">
                        <Label htmlFor="state-select">Building Code State <span className="text-red-500">*</span></Label>
                        <Combobox
                            options={US_STATES}
                            value={selectedState}
                            onValueChange={setSelectedState}
                            placeholder="Select a state..."
                            searchPlaceholder="Search states..."
                            emptyText="No state found."
                        />
                        <p className="text-xs text-muted-foreground">
                            {defaultState
                                ? `Detected ${US_STATES.find(s => s.value === defaultState)?.label || defaultState} from property address. You can change this if needed.`
                                : "Select the state for building code justifications. Required for supplement creation."
                            }
                        </p>
                    </div>

                    {/* Pricing Breakdown - shown when comparison is selected and estimate is available */}
                    {selectedComparison && selectedStats && costEstimate && (
                        <SupplementPricing
                            unmatchedLineItems={selectedStats.unmatchedContractorCount || 0}
                            costCents={costEstimate.costCents}
                            tierCode={costEstimate.tierCode}
                            className="shrink-0"
                        />
                    )}
                </div>

                <DialogFooter className="px-6 py-4 border-t shrink-0">
                    <Button
                        variant="outline"
                        onClick={() => handleOpenChange(false)}
                        disabled={isCreating}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCreateSupplement}
                        disabled={!canCreate}
                    >
                        {isCreating ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Creating Supplement...
                            </>
                        ) : (
                            'Create Supplement'
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
